import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCupRounds } from '@/hooks/useCupRounds';
import { cupRoundLabel } from '@/lib/competition/cupRounds';
import { type RoundKey, compareRoundKeys } from '@/lib/competition/roundBuckets';

/**
 * Outils des écrans regroupés par tour (lib/competition/roundBuckets) : tri des tas et
 * libellé d'un tour de coupe. Pendant le chargement de cup_rounds, un tour connu
 * s'affiche « … » — jamais « Tour non précisé » pour un match qui a un tour.
 * Les journées gardent les libellés de chaque écran (inchangés).
 */
export function useRoundBuckets() {
  const { rounds, isLoading } = useCupRounds();
  const { t, i18n } = useTranslation();
  const lang: 'fr' | 'ar' = i18n.language === 'ar' ? 'ar' : 'fr';

  /** Libellé d'un tour (null = non précisé) ; `unknownLabel` remplace « Tour non précisé ». */
  const cupLabel = useCallback(
    (round: string | null, variant: 'full' | 'short' = 'full', unknownLabel?: string): string => {
      if (round && isLoading) return '…';
      return cupRoundLabel(round, rounds, lang, variant) ?? unknownLabel ?? t('common:cup.round_unknown');
    },
    [rounds, isLoading, lang, t],
  );

  const compare = useCallback((a: RoundKey, b: RoundKey) => compareRoundKeys(a, b, rounds), [rounds]);

  return { rounds, isLoading, cupLabel, compare };
}
